import { clienteDisplay } from '@/lib/types';
import { useDraggable } from '@dnd-kit/core';
import { CSS } from '@dnd-kit/utilities';
import clsx from 'clsx';
import { CheckSquare, Handshake, Users, CalendarCheck } from 'lucide-react';
import type { Demanda, SubEtapa, StatusDemanda, SubItemTipoB } from '@/lib/types';

const FLUXO_POR_STATUS: Partial<Record<StatusDemanda, SubEtapa[]>> = {
  'Triagem':              ['Briefing inicial', 'Pauta', 'Pernas / marcação'],
  'Em produção':          ['Agendamento', 'Captação', 'Roteiro', 'Edição'],
  'Em revisão':           ['Revisão técnica', 'Revisão editorial'],
  'Aguardando aprovação': ['Cliente aprovando', 'Ajustes finais'],
  'Publicada':            ['Publicado'],
};

function subEtapaProgress(status: StatusDemanda, sub?: SubEtapa): { atual: number; total: number } | null {
  const fluxo = FLUXO_POR_STATUS[status];
  if (!fluxo || !sub) return null;
  const idx = fluxo.indexOf(sub);
  if (idx < 0) return null;
  return { atual: idx + 1, total: fluxo.length };
}

interface Props {
  demanda: Demanda;
  solicitanteNome?: string;
  areasNomes: string[];
  onClick?: () => void;
}

export function DemandaCard({ demanda, solicitanteNome, areasNomes, onClick }: Props) {
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
    id: demanda.id,
    data: { status: demanda.status },
  });

  const style = {
    transform: CSS.Translate.toString(transform),
    opacity: isDragging ? 0.4 : 1,
  };

  const prioColor = {
    'Crítica': 'bg-red',
    'Alta':    'bg-red/60',
    'Média':   'bg-orange/60',
    'Baixa':   'bg-green/60',
  }[demanda.prioridade] ?? 'bg-muted';

  const slaAtrasado = demanda.prazoSla && new Date(demanda.prazoSla) < new Date();
  const slaTexto = demanda.prazoSla
    ? new Date(demanda.prazoSla).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' })
    : '';

  const areaPrimaria = areasNomes[0];
  const prog = demanda.subEtapa ? subEtapaProgress(demanda.status, demanda.subEtapa) : null;
  const isChecklist = demanda.tipoEntrega === 'checklist';
  const isComercial = demanda.tipoEntrega === 'comercial';
  const isTipoB = demanda.categoriaDemanda === 'tipo_b';
  const subItems: SubItemTipoB[] = isTipoB && demanda.subItemsTipoB
    ? (() => { try { return JSON.parse(demanda.subItemsTipoB); } catch { return []; } })()
    : [];
  const subConcluidos = subItems.filter((s) => s.status === 'concluido').length;

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      onClick={onClick}
      className={clsx(
        'group bg-panel-2 border border-border rounded px-2 py-1.5 cursor-grab active:cursor-grabbing relative',
        'hover:border-border-light transition-colors',
        isDragging && 'shadow-2xl ring-1 ring-blue/40',
        isChecklist && 'opacity-60',
        isComercial && 'border-l-2 border-l-green/50',
        isTipoB && 'border-l-2 border-l-purple/50',
      )}
    >
      {/* Priority bar à esquerda */}
      <div className={clsx('absolute left-0 top-1 bottom-1 w-0.5 rounded-full', prioColor)} />

      <div className="pl-1.5">
        <div className="flex items-start gap-1">
          <h3 className="text-[11px] font-semibold text-text leading-tight line-clamp-2 flex-1">
            {demanda.titulo || '(sem título)'}
          </h3>
          {isChecklist && <CheckSquare className="w-3 h-3 text-muted shrink-0 mt-0.5" title="Checklist" />}
          {isComercial && <Handshake className="w-3 h-3 text-green shrink-0 mt-0.5" title="Comercial" />}
          {isTipoB && <CalendarCheck className="w-3 h-3 text-purple shrink-0 mt-0.5" title="Tipo B · Evento" />}
        </div>

        {isTipoB && subItems.length > 0 ? (
          <div className="flex items-center gap-1.5 mt-1 text-[9px]">
            <span className="inline-flex items-center gap-0.5 px-1 py-0.5 rounded bg-purple/10 text-purple border border-purple/20 font-semibold">
              <Users className="w-2.5 h-2.5" />
              {subItems.length} clientes
            </span>
            <span className="text-muted">{subConcluidos}/{subItems.length}</span>
            {slaTexto && <span className={clsx('shrink-0 ml-auto', slaAtrasado ? 'text-red font-medium' : 'text-muted')}>{slaTexto}</span>}
          </div>
        ) : (
          <div className="flex items-center justify-between gap-1 mt-1 text-[9px] text-muted">
            {solicitanteNome && <span className="truncate flex-1">{clienteDisplay(solicitanteNome)}</span>}
            {slaTexto && <span className={clsx('shrink-0', slaAtrasado ? 'text-red font-medium' : '')}>{slaTexto}</span>}
          </div>
        )}

        {prog && (
          <div className="flex gap-0.5 mt-1">
            {Array.from({ length: prog.total }).map((_, i) => (
              <div key={i} className={clsx('h-0.5 flex-1 rounded-full', i < prog.atual ? 'bg-blue-light' : 'bg-border')} />
            ))}
          </div>
        )}

        {areaPrimaria && (
          <div className="mt-1 text-[9px] text-blue-light/80 truncate">
            {areaPrimaria.length > 16 ? areaPrimaria.slice(0, 15) + '…' : areaPrimaria}
          </div>
        )}

        {demanda.metricas && (demanda.metricas.views || demanda.metricas.engajamento) && (
          <div className="flex items-center gap-2 mt-1 text-[9px] text-muted">
            {demanda.metricas.views != null && <span>👁 {demanda.metricas.views.toLocaleString('pt-BR')}</span>}
            {demanda.metricas.engajamento != null && <span>📊 {demanda.metricas.engajamento}%</span>}
          </div>
        )}
      </div>
    </div>
  );
}
